import React, { memo, useMemo, useState } from "react";

import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { ToggleButton, ToggleButtonChangeEvent } from "primereact/togglebutton";
import { T } from "services/lang/LanguageService";
import useScadaStore from "store/scada-store";
import { LabelLayer, ScadaProps } from "../scadaTypes";
import { DEFAULT_LAYER_NAME, defaultLayer } from "../utils/default-values";

type ScadaLabelsProps = {
  currentConfig: ScadaProps;
  setCurrentConfig: React.Dispatch<React.SetStateAction<ScadaProps>>;
  onConfigChange: (config: ScadaProps) => void;
};

const ScadaLabels: React.FC<ScadaLabelsProps> = ({ currentConfig, setCurrentConfig, onConfigChange }) => {
  const [newLayer, setNewLayer] = useState<LabelLayer>(defaultLayer);

  const updateScadaLabelLayers = useScadaStore(state => state.updateScadaLabelLayers);
  const updateScadaLabels = useScadaStore(state => state.updateScadaLabels);

  const labelLayers = useMemo(() => [...(currentConfig.labelLayers ?? [])], [currentConfig.labelLayers]);

  const handleLabelLayerChange = (e: ToggleButtonChangeEvent, layer: string) => {
    const updLayer = labelLayers.map(lg => (lg.layer === layer ? { ...lg, visible: e.value } : lg));
    updateScadaLabelLayers(currentConfig.id!, updLayer);
    setCurrentConfig({ ...currentConfig, labelLayers: updLayer });
    onConfigChange({ ...currentConfig, labelLayers: updLayer });
  };

  const addLabelLayer = () => {
    if (labelLayers.find(lg => lg.layer === newLayer.layer)) return;
    const updLayer = [...labelLayers, newLayer];
    updateScadaLabelLayers(currentConfig.id!, updLayer);
    setCurrentConfig({ ...currentConfig, labelLayers: updLayer });
    onConfigChange({ ...currentConfig, labelLayers: updLayer });
    setNewLayer(defaultLayer);
  };

  const removeLabelLayer = (index: number) => {
    const removedLayer = labelLayers.splice(index, 1);
    const updatedElements = currentConfig.elements.map(el =>
      el.layer === removedLayer[0].layer ? { ...el, layer: DEFAULT_LAYER_NAME } : el
    );
    updateScadaLabels(currentConfig.id!, updatedElements);
    updateScadaLabelLayers(currentConfig.id!, labelLayers);
    setCurrentConfig({ ...currentConfig, labelLayers, elements: updatedElements });
    onConfigChange({ ...currentConfig, labelLayers, elements: updatedElements });
  };

  const mappedLabelLayers = [...labelLayers].map((lg, index) => (
    <div className="p-inputgroup w-8 mb-3" key={lg.layer}>
      <InputText value={lg.layer} disabled />
      <div className="p-inputgroup-addon py-1">
        <ToggleButton
          onLabel={T("word.visible")}
          offLabel={T("word.hidden")}
          checked={lg.visible}
          onChange={e => handleLabelLayerChange(e, lg.layer)}
        />
      </div>
      <Button
        icon="pi pi-times"
        type="button"
        onClick={() => removeLabelLayer(index)}
        className="p-inputgroup-addon p-button-sm p-button-danger"
        disabled={lg.layer === DEFAULT_LAYER_NAME}
      />
    </div>
  ));

  return (
    <>
      {mappedLabelLayers}
      <div className="p-inputgroup w-8">
        <InputText
          placeholder={T("action.add-new-layer")}
          value={newLayer.layer}
          onChange={e =>
            setNewLayer(prev => ({
              ...prev,
              layer: e.target.value.trimStart(),
            }))
          }
        />
        <div className="p-inputgroup-addon py-1">
          <ToggleButton
            onLabel={T("word.visible")}
            offLabel={T("word.hidden")}
            checked={newLayer.visible}
            onChange={e => setNewLayer(prev => ({ ...prev, visible: e.value }))}
          />
        </div>
        <Button
          icon="pi pi-check"
          onClick={addLabelLayer}
          type="button"
          className="p-inputgroup-addon p-button-sm p-button-success"
        />
      </div>
    </>
  );
};

export default memo(ScadaLabels, (prevProps, nextProps) => {
  return (
    JSON.stringify(prevProps.currentConfig.labelLayers) ===
    JSON.stringify(nextProps.currentConfig.labelLayers)
  );
});
